import { Fragment, useState } from "react";
import { ComboPackageType } from "../../interfaces/comboPackage/ComboPackage.type";
import styles from "./ComboPackageGroup.module.css";
import useComponentsStore from "../../store/componentsStateStore";
import useHintStore from "../../store/hintStateStore";
import {
  getGeneralImage,
  generateStyleClasses,
  styleHelper,
} from "../../helpers/commonFunctions";
import { logOrderProductsClickAddCart } from "../../membership/logging/helpers/commonLoggingFunctions";
import useAppStore from "../../store/AppStore";

export const textFormatter = (text: string, fontSize?: string) => {
  if (!text) return null;
  const newText = text?.split("*").map((part, idx) =>
    idx % 2 === 0 ? (
      <Fragment key={idx}>{part}</Fragment>
    ) : (
      <strong className={`vas ${fontSize ? fontSize : "vas-fs-2"}`} key={idx}>
        {part}
      </strong>
    )
  );
  return newText;
};

export default function ComboPackageGroup({
  item,
  scroll,
}: {
  item: ComboPackageType;
  scroll?: Boolean;
}) {
  const topPill = item?.topPill;
  const title = item?.title;
  const hint = title?.hint;
  const dashedSeparator = item?.dashedSeparator;
  const featuresList = item?.featuresList;
  const footer = item?.footer;
  const actionButton = footer?.actionButton;
  const strikeThroughUnitPrice = footer?.strikeThroughUnitPrice;
  const [isLoading, setIsLoading] = useState(false);
  const { continueDraft } = useComponentsStore();
  const { setOpen } = useHintStore();
  const { isDeviceMobile } = useAppStore();

  const openModal = () => {
    setOpen(true, hint?.link);
  };

  const onClickHandler = async () => {
    try {
      logOrderProductsClickAddCart();
      setIsLoading(true);
      await continueDraft(actionButton.payload);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
    }
  };

  return (
    <div className="vas-d-flex vas-flex-column vas-align-items-center">
      <div
        className={`vas-px-3 vas-text-center vas-py-1 vas-fw-bold vas-border-radius ${styles.consumer_tag}`}
        style={styleHelper({
          color: topPill?.textColor ?? "",
          backgroundColor: topPill?.backgroundColor,
          border: topPill ? `1px solid ${topPill?.borderColor}}` : "0px",
        })}
      >
        {topPill?.text ?? ""}
      </div>
      <div
        className={`vas-d-flex vas-flex-column vas-px-3 vas-pt-3 vas-card ${
          scroll && isDeviceMobile
            ? styles.combo_package_scroll_mobile
            : styles.combo_package
        }`}
        style={styleHelper({
          border: `1px solid ${item?.borderColor}`,
        })}
      >
        <div className="vas-my-1">
          <div className="vas-d-flex vas-justify-content-between vas-align-items-center vas-mt-1">
            <span
              className="vas-fs-4 vas-fw-bold"
              style={styleHelper({ color: title?.textColor })}
            >
              {title?.text ?? ""}
            </span>
            <span
              className={`${generateStyleClasses(
                hint?.style
              )} vas-fs-7 pointerCursor`}
              style={styleHelper({
                color: hint?.textColor,
              })}
              onClick={openModal}
            >
              {hint?.text ?? ""}
            </span>
          </div>
          <span className={`vas-fs-5 ${styles.more_views}`}>
            {title &&
              title.subtitleTextParts &&
              title.subtitleTextParts.map((text, idx: number) => {
                return (
                  <span
                    key={idx}
                    className={`vas-fs-6 ${generateStyleClasses(text?.style)}`}
                    style={styleHelper({ color: text?.textColor })}
                  >
                    {text?.text ?? ""}
                    {idx % 2 === 0 && " "}
                  </span>
                );
              })}
          </span>
        </div>
        <span
          className={`vas-dotted-line vas-py-1 vas-my-2`}
          style={styleHelper({ borderColor: dashedSeparator?.color })}
        />
        <div className="vas-fs-5 vas-pt-2 vas-d-flex vas-flex-column vas-gap-2">
          {featuresList &&
            featuresList.map((feature, idx: number) => {
              return (
                <span className="vas-fs-6" key={idx}>
                  {feature.icon && (
                    <img
                      src={getGeneralImage(feature.icon)}
                      alt={`${idx}_Feature_icon`}
                      width={28}
                      height={28}
                      className="vas vas-mx-1"
                    />
                  )}
                  {textFormatter(feature.text, "vas-fs-6")}
                </span>
              );
            })}
        </div>
        <span
          className={`vas-dotted-line vas-py-1 vas-mt-2`}
          style={styleHelper({ borderColor: dashedSeparator?.color })}
        />
        <div
          className={`vas-d-flex vas-flex-column vas-justify-content-center ${styles.save_holder}`}
        >
          <span
            className={`vas-d-block vas-fs-7 vas-text-decoration-line-through ${styles.old_price_holder}`}
            style={styleHelper({ color: strikeThroughUnitPrice?.textColor })}
          >
            {strikeThroughUnitPrice?.text ?? ""}
          </span>
          <div className="vas-d-flex vas-justify-content-between">
            <span className="vas-d-block vas-my-1 vas-fs-6">
              {textFormatter(footer?.unitPrice?.text ?? "", "vas-text-14")}
            </span>
            {footer?.save?.text ? (
              <div
                className={`vas-rounded-3 vas-text-success vas-px-2 vas-fw-bold vas-text-center vas-align-self-center ${styles.combo_save_tag}`}
                style={{
                  border: `1px solid ${footer?.save?.borderColor}`,
                  backgroundColor: footer?.save?.backgroundColor,
                }}
              >
                <div
                  className={`vas-fs-7 ${generateStyleClasses(
                    footer?.save?.style
                  )}`}
                  style={{ color: footer?.save?.textColor, marginTop: "1px" }}
                >
                  {footer?.save?.text ?? ""}
                </div>
              </div>
            ) : (
              <div />
            )}
          </div>

          {footer?.totalPrice?.text ? (
            <p
              className={`vas-fs-5 vas-m-0 ${generateStyleClasses(
                footer?.totalPrice?.style
              )}`}
              style={styleHelper({
                color: footer?.totalPrice?.textColor,
              })}
            >
              {footer?.totalPrice?.text ?? ""}
            </p>
          ) : (
            <div className={styles.total_price_holder} />
          )}
        </div>

        <div className="vas-w-100 vas-d-flex vas-mb-3 vas-align-items-center">
          <button
            className={`vas-btn vas-btn-lg vas-fs-6 vas-fw-bold vas-w-100 vas-border-radius`}
            style={styleHelper({
              color: actionButton?.textColor,
              backgroundColor: actionButton?.backgroundColor,
            })}
            onClick={onClickHandler}
          >
            {isLoading ? (
              <i className="vas-spinner-border vas-spinner-border-sm vas-p-2" />
            ) : (
              <>{actionButton?.text ?? ""}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
